export interface ZohoOAuthCallbackDraft {
  code: string | null
  state: string | null
  error: string | null
  errorDescription: string | null
  capturedAt: number
}

export type ZohoOAuthCallbackParams = Omit<ZohoOAuthCallbackDraft, 'capturedAt'>

const STORAGE_KEY = 'tlaaq_zoho_oauth_callback'

const EMPTY_CALLBACK: ZohoOAuthCallbackParams = {
  code: null,
  state: null,
  error: null,
  errorDescription: null,
}

function canUseSessionStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined'
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function normalizeDraft(value: unknown): ZohoOAuthCallbackDraft | null {
  if (!isRecord(value)) return null
  const capturedAt = typeof value.capturedAt === 'number' ? value.capturedAt : Date.now()
  const draft: ZohoOAuthCallbackDraft = {
    code: typeof value.code === 'string' ? value.code : null,
    state: typeof value.state === 'string' ? value.state : null,
    error: typeof value.error === 'string' ? value.error : null,
    errorDescription: typeof value.errorDescription === 'string' ? value.errorDescription : null,
    capturedAt,
  }
  return hasCallbackParams(draft) ? draft : null
}

function paramsFromUrl(url: URL): ZohoOAuthCallbackParams {
  return {
    code: url.searchParams.get('code'),
    state: url.searchParams.get('state'),
    error: url.searchParams.get('error'),
    errorDescription: url.searchParams.get('error_description'),
  }
}

function isZohoOAuthUrl(url: URL): boolean {
  const normalizedPath = url.pathname.replace(/\/+$/, '') || '/'
  return normalizedPath === '/zoho-oauth'
}

export function hasCallbackParams(params: ZohoOAuthCallbackParams): boolean {
  return Boolean(params.code || params.state || params.error || params.errorDescription)
}

export function saveZohoOAuthCallback(params: ZohoOAuthCallbackParams): ZohoOAuthCallbackDraft | null {
  if (!hasCallbackParams(params)) return null
  const draft: ZohoOAuthCallbackDraft = { ...params, capturedAt: Date.now() }
  if (!canUseSessionStorage()) return draft
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft))
  } catch {
    return draft
  }
  return draft
}

export function captureZohoOAuthCallbackFromUrl(href: string = typeof window === 'undefined' ? '' : window.location.href): ZohoOAuthCallbackDraft | null {
  if (!href) return null
  const url = new URL(href)
  if (!isZohoOAuthUrl(url)) return null
  return saveZohoOAuthCallback(paramsFromUrl(url))
}

export function readStoredZohoOAuthCallback(): ZohoOAuthCallbackDraft | null {
  if (!canUseSessionStorage()) return null
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return normalizeDraft(JSON.parse(raw))
  } catch {
    return null
  }
}

export function hasStoredZohoOAuthCallback(): boolean {
  return readStoredZohoOAuthCallback() !== null
}

export function readZohoOAuthCallbackParams(): ZohoOAuthCallbackParams {
  if (typeof window !== 'undefined') {
    const url = new URL(window.location.href)
    const fromUrl = paramsFromUrl(url)
    if (isZohoOAuthUrl(url) && hasCallbackParams(fromUrl)) {
      saveZohoOAuthCallback(fromUrl)
      return fromUrl
    }
  }

  const stored = readStoredZohoOAuthCallback()
  if (stored) {
    return {
      code: stored.code,
      state: stored.state,
      error: stored.error,
      errorDescription: stored.errorDescription,
    }
  }

  return EMPTY_CALLBACK
}

export function clearZohoOAuthCallback(): void {
  if (!canUseSessionStorage()) return
  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Ignore storage failures; the callback URL is stripped after success too.
  }
}
